import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, CssBaseline, Box, Button, Container, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import profile from './profileData';
import Hero from './components/Hero';
import ResearchInterests from './components/Skills';
import Papers from './components/Papers';
import Timeline from './components/Timeline';
import Contact from './components/Contact';
import Awards from './components/Awards';
import Teaching from './components/Teaching';
import Seminars from './components/Seminars';

const sections = [
  { id: 'about', label: 'About' },
  { id: 'skills', label: 'Research Interests' },
  { id: 'awards', label: 'Honors, Awards, and Grants' },
  // { id: 'experience', label: 'Professional Experience' },
  { id: 'papers', label: 'Publications' },
  { id: 'teaching', label: 'Teaching Experience' },
  { id: 'seminars', label: 'Seminars & Conferences' },
  { id: 'education', label: 'Education' },
  { id: 'cv', label: 'CV', href: profile.cvUrl },
  { id: 'contact', label: 'Contact' },
];

// Width at which every nav label fits on one line next to the name.
const fullNavQuery = '@media (min-width: 1380px)';

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

function App() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleNavClick = (id) => {
    scrollToSection(id);
    setDrawerOpen(false);
  };

  return (
    <>
      <CssBaseline />
      <AppBar position="fixed" color="primary" elevation={2} sx={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(25, 118, 210, 0.95)' }}>
        <Toolbar sx={{ position: 'relative' }}>
          {/* Mobile nav: hamburger on left */}
          <Box sx={{ display: 'flex', mr: 1, [fullNavQuery]: { display: 'none' } }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
              aria-label="open navigation menu"
              aria-controls={drawerOpen ? 'mobile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={drawerOpen ? 'true' : undefined}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: 1, textAlign: 'left', cursor: 'pointer' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            {profile.name}
          </Typography>
          {/* Desktop nav */}
          <Box sx={{ flexGrow: 1, display: 'none', ml: 2, gap: 0.5, [fullNavQuery]: { display: 'flex' } }}>
            {sections.map((section) => (
              <Button
                key={section.id}
                {...(section.href
                  ? { href: section.href, target: '_blank', rel: 'noopener noreferrer' }
                  : { onClick: () => handleNavClick(section.id) })}
                sx={{ 
                  color: 'white',
                  display: 'block',
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {section.label}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: '80vw',
            maxWidth: 340,
            top: { xs: '56px', sm: '64px' },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
          }
        }}
      >
        <List>
          {sections.map((section) => (
            <ListItem key={section.id} disablePadding>
              <ListItemButton
                {...(section.href
                  ? { component: 'a', href: section.href, target: '_blank', rel: 'noopener noreferrer', onClick: () => setDrawerOpen(false) }
                  : { onClick: () => handleNavClick(section.id) })}
              >
                <ListItemText 
                  primary={section.label} 
                  sx={{ 
                    '& .MuiTypography-root': {
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Toolbar /> {/* Spacer for fixed AppBar */}
      <Hero
        name={profile.name}
        title={profile.title}
        about={profile.about}
        profileImage={profile.profileImage}
        social={profile.social}
        cvUrl={profile.cvUrl}
        layoutSideBySide
      />
      <Container maxWidth="lg" sx={{ px: { xs: 1, md: 2 } }}>
        <ResearchInterests skills={profile.skills} />
        <Papers />
        <Awards />
        {/* <Experience /> */}
        <Teaching />
        <Seminars />
        <Timeline timeline={profile.timeline} />
        <Contact email={profile.email} social={profile.social} />
      </Container>
      <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', py: 2, textAlign: 'center' }}>
        <Typography variant="body2">&copy; {new Date().getFullYear()} {profile.name}. All rights reserved.</Typography>
      </Box>
    </>
  );
}

export default App;
